import { inject, provide, reactive, computed, ComputedRef } from "vue";
import { ParlayStates } from "@fresh8/types/src/parlayStates";
import { BetDataContext } from "./betData";
import { calcParlayAccumulator } from "./lib/parlayCalculation";
import clonedeep from "lodash.clonedeep";
import { BetSelection } from "@fresh8/types/src/betSelection";

export const parlayStatesSymbol = Symbol("parlayStatesSymbol");

type CreatedParlayStates = {
  updateSelections: (selection: BetSelection) => void;
  findSelection: (selection: BetSelection) => number;
  hasSelections: () => boolean;
  getSelections: ComputedRef<BetSelection[]>;
  parlayAccumulator: ComputedRef<number>;
  clearSelections: () => void;
};

export function createParlayStates(
  betData: BetDataContext
): CreatedParlayStates {
  const state = reactive({
    selections: []
  }) as ParlayStates;

  const getSelections: ComputedRef<BetSelection[]> = computed(
    () => state.selections
  );

  const updateSelections: (selection: BetSelection) => void = selection => {
    let newSelections = clonedeep(state.selections);
    const { eventId, marketId, optionId } = selection;

    const selectionIndex = findSelection(selection);
    if (selectionIndex !== -1) {
      newSelections.splice(selectionIndex, 1);
    } else {
      newSelections = newSelections.filter(
        selection =>
          !(eventId === selection.eventId && marketId === selection.marketId)
      );
      newSelections.push({
        eventId,
        marketId,
        optionId
      });
    }
    state.selections = newSelections;
  };

  const findSelection: (selection: BetSelection) => number = selection => {
    return state.selections.findIndex(
      ({ eventId, marketId, optionId }) =>
        eventId === selection.eventId &&
        marketId === selection.marketId &&
        optionId === selection.optionId
    );
  };

  const hasSelections: () => boolean = () => !!state.selections.length;

  const clearSelections = (): void => {
    state.selections = [];
  };

  const parlayAccumulator = computed(() => {
    const options = betData.options.value;
    if (!hasSelections()) {
      return 0;
    }
    const fractionalOdds = state.selections.map(
      selection => options?.[selection.optionId]?.odds || []
    );
    return calcParlayAccumulator(fractionalOdds);
  });

  return {
    updateSelections,
    findSelection,
    hasSelections,
    getSelections,
    parlayAccumulator,
    clearSelections
  };
}

export type parlayStatesContext = ReturnType<typeof createParlayStates>;

export function provideParlayStates(betData: BetDataContext): void {
  provide(parlayStatesSymbol, createParlayStates(betData));
}

export function injectParlayStates(): CreatedParlayStates {
  const state = inject<parlayStatesContext>(parlayStatesSymbol);

  if (!state) return {} as CreatedParlayStates;

  return state;
}
